import { Component, OnDestroy, AfterViewInit, Output, EventEmitter, ElementRef, Input } from '@angular/core';
import { LocationStrategy } from '@angular/common';

@Component({
  selector: 'ngx-tiny-mce',
  template: '<textarea [id]="editorId"></textarea>',
})
export class TinyMCEComponent implements OnDestroy, AfterViewInit {
  @Input()
  menubar: boolean;

  @Input()
  toolbar: string | boolean;

  @Input()
  statusbar: boolean;

  @Input()
  css?: string;

  @Input()
  placeholder?: string;

  // When true, plain Enter is swallowed and emitted as editorEnter; Shift+Enter still inserts a newline.
  @Input()
  submitOnEnter = false;

  @Output() editorKeyup = new EventEmitter<any>();

  @Output() editorEnter = new EventEmitter<string>();

  editor: any;

  editorId = 'tiny-' + Math.random().toString(36).substring(2, 9);

  constructor(
    private host: ElementRef,
    private locationStrategy: LocationStrategy,
  ) { }

  ngAfterViewInit() {
    if (tinymce.get(this.editorId)) {
      tinymce.get(this.editorId).remove();
    }

    // iOS Safari zooms the page when an editable under 16px takes focus and never zooms back.
    // Measured on the page, not in content_style: a media query there sees the iframe's width.
    const bodyFontSize = window.matchMedia('(max-width: 767.98px)').matches ? 16 : 14;

    const options: any = {
      selector: `#${this.editorId}`,
      target: this.host.nativeElement,
      plugins: ['link', 'paste', 'table'],
      skin_url: `${this.locationStrategy.getBaseHref()}assets/skins/lightgray`,
      setup: editor => {
        this.editor = editor;
        editor.on('keyup', () => {
          this.editorKeyup.emit(editor.getContent());
        });
        editor.on('keydown', (event: KeyboardEvent) => {
          if (!this.submitOnEnter || event.key !== 'Enter') {
            return;
          }
          // Enter commits an open IME candidate, it doesn't end the message (229 = legacy isComposing).
          if (event.isComposing || event.keyCode === 229) {
            return;
          }
          if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
            return;
          }
          event.preventDefault();
          this.editorEnter.emit(editor.getContent());
        });
        editor.on('ExecCommand', () => {
          this.editorKeyup.emit(editor.getContent());
        });
        // Fires on caret move/selection change too, so format-button active state
        // stays in sync when the user clicks into already-formatted text.
        editor.on('NodeChange', () => {
          this.editorKeyup.emit(editor.getContent());
        });
      },
      content_style: `
      body {
        font-family: "Varela Round", sans-serif;
        font-size: ${bodyFontSize}px;
        color: #111827;
        background-color: #FFFFFF;
        padding: 10px 13px;
        margin: 0;
      }
      p {
        margin: 0;
      }
      a {
        color: #2563EB;
        text-decoration: underline;
      }`,
      height: 100,
      placeholder: this.placeholder || 'test place',
    };
    if (this.menubar !== undefined) {
      options.menubar = this.menubar;
    }
    if (this.toolbar !== undefined) {
      options.toolbar = this.toolbar;
    }
    if (this.statusbar !== undefined) {
      options.statusbar = this.statusbar;
    }
    tinymce.init(options);

  }

  ngOnDestroy() {
    tinymce.remove(this.editor);
  }

  setContent(content: string): void {
    tinymce.activeEditor.setContent(content);
  }
}
